import { AxiosError } from 'axios'
import type { User } from 'firebase/auth'
import type {
  DocumentData,
  ReferenceDocument,
  ResourceType,
  Folder,
  CopyDocumentResponse,
} from './entities'
import { BASE_URL } from './constants'
import { getUserById, type Data, type Name } from './queries'
import client, { WithSpan } from './client'

// Centralized mutation keys
export const mutationKeys = {
  documents: {
    create: (tenantId: string, projectId: string) =>
      ['documents', 'create', tenantId, projectId] as const,
    update: (tenantId: string, projectId: string, docId: string) =>
      ['documents', 'update', tenantId, projectId, docId] as const,
    delete: (tenantId: string, projectId: string, docId: string) =>
      ['documents', 'delete', tenantId, projectId, docId] as const,
    process: (tenantId: string, projectId: string, docId: string) =>
      ['documents', 'process', tenantId, projectId, docId] as const,
    save: (tenantId: string, projectId: string, docId: string) =>
      ['documents', 'save', tenantId, projectId, docId] as const,
    syncSOPRag: (tenantId: string, projectId: string, docId: string) =>
      ['documents', 'sync-sop-rag', tenantId, projectId, docId] as const,
    move: (tenantId: string, projectId: string, docId: string) =>
      ['documents', 'move', tenantId, projectId, docId] as const,
    exportXlsx: (tenantId: string, projectId: string, sopId: string) =>
      ['documents', 'export-xlsx', tenantId, projectId, sopId] as const,
  },
  referenceDocuments: {
    create: (tenantId: string, projectId: string) =>
      ['reference-documents', 'create', tenantId, projectId] as const,
    update: (tenantId: string, projectId: string, docId: string) =>
      ['reference-documents', 'update', tenantId, projectId, docId] as const,
    delete: (tenantId: string, projectId: string, docId: string) =>
      ['reference-documents', 'delete', tenantId, projectId, docId] as const,
    uploadUrl: (tenantId: string, projectId: string, docId: string) =>
      [
        'reference-documents',
        'upload-url',
        tenantId,
        projectId,
        docId,
      ] as const,
    syncRag: (tenantId: string, projectId: string, docId: string) =>
      ['reference-documents', 'sync-rag', tenantId, projectId, docId] as const,
  },
  projects: {
    create: (tenantId: string) => ['projects', 'create', tenantId] as const,
    rename: (tenantId: string, projectId: string) =>
      ['projects', 'rename', tenantId, projectId] as const,
    delete: (tenantId: string, projectId: string) =>
      ['projects', 'delete', tenantId, projectId] as const,
    updateUserAccess: (tenantId: string, projectId: string) =>
      ['projects', 'update-user-access', tenantId, projectId] as const,
  },
  folders: {
    create: (tenantId: string, projectId: string) =>
      ['folders', 'create', tenantId, projectId] as const,
    rename: (tenantId: string, projectId: string, folderName: string) =>
      ['folders', 'rename', tenantId, projectId, folderName] as const,
    delete: (tenantId: string, projectId: string, folderName: string) =>
      ['folders', 'delete', tenantId, projectId, folderName] as const,
  },
  users: {
    create: (tenantId: string) => ['users', 'create', tenantId] as const,
    invite: (tenantId: string) => ['users', 'invite', tenantId] as const,
    updateRole: (tenantId: string, userId: string) =>
      ['users', 'update-role', tenantId, userId] as const,
    updateProfile: (tenantId: string) =>
      ['users', 'update-profile', tenantId] as const,
    delete: (tenantId: string) => ['users', 'delete', tenantId] as const,
    updateWorkspaceAccess: (tenantId: string, userId: string) =>
      ['users', 'update-workspace-access', tenantId, userId] as const,
    revokeOrgRole: (tenantId: string, userId: string) =>
      ['users', 'revoke-org-role', tenantId, userId] as const,
  },
  bookmarks: {
    create: (
      tenantId: string,
      resourceType: ResourceType,
      resourceId: string,
    ) => ['bookmarks', 'create', tenantId, resourceType, resourceId] as const,
    delete: (
      tenantId: string,
      resourceType: ResourceType,
      resourceId: string,
    ) => ['bookmarks', 'delete', tenantId, resourceType, resourceId] as const,
  },
  settings: {
    update<S>(tenantId: string, name: Name<S>) {
      return ['settings', 'update', tenantId, name] as const
    },
  },
  copy: {
    document: (tenantId: string, docId: string) =>
      ['copy', 'document', tenantId, docId] as const,
    sop: (tenantId: string, sopId: string) =>
      ['copy', 'sop', tenantId, sopId] as const,
  },
} as const

interface MutationAuthHeaders extends Record<string, string> {
  Accept: string
  'Content-Type': string
  Authorization: string
}

interface PutAuthHeaders extends Record<string, string> {
  Accept: string
  Authorization: string
}

export const getMutationAuthHeaders = WithSpan(
  'getMutationAuthHeaders',
  getMutationAuthHeadersInternal,
)

// For mutations, fetch fresh id token from auth state so that we don't use stale token
async function getMutationAuthHeadersInternal(
  authState: User,
): Promise<MutationAuthHeaders> {
  const token = await authState.getIdToken()
  if (!token) {
    throw new Error('No token found')
  }
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

export const getPutAuthHeaders = WithSpan(
  'getPutAuthHeaders',
  getPutAuthHeadersInternal,
)

async function getPutAuthHeadersInternal(
  authState: User,
): Promise<PutAuthHeaders> {
  const token = await authState.getIdToken()
  if (!token) {
    throw new Error('No token found')
  }
  return {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

interface CreateReferenceDocumentParams {
  authState: User
  tenantId: string
  projectId: string
  data: {
    collection: string
    description: string
    metadata: {
      filename: string
      size: number
      mimeType?: string
      transcode?: Record<string, unknown>
    }
    modelName?: string
  }
}

export const createReferenceDocument = WithSpan(
  'createReferenceDocument',
  createReferenceDocumentInternal,
)

async function createReferenceDocumentInternal({
  authState,
  tenantId,
  projectId,
  data,
}: CreateReferenceDocumentParams): Promise<ReferenceDocument> {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents`,
      data,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Create new reference document error:', error)
    throw new Error('Failed to create new reference document')
  }
}

interface DeleteReferenceDocumentParams {
  authState: User
  tenantId: string
  projectId: string
  referenceDocumentId: string
}

export const deleteReferenceDocument = WithSpan(
  'deleteReferenceDocument',
  deleteReferenceDocumentInternal,
)

async function deleteReferenceDocumentInternal({
  authState,
  tenantId,
  projectId,
  referenceDocumentId,
}: DeleteReferenceDocumentParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.delete(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${referenceDocumentId}`,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Delete reference document error:', error)
    throw new Error('Failed to delete reference document')
  }
}

interface UpdateReferenceDocumentParams {
  authState: User
  tenantId: string
  projectId: string
  referenceDocumentId: string
  description?: string
  data: {
    metadata?: {
      filename: string
      size: number
      mimeType?: string
      transcode?: Record<string, unknown>
    }
    folder?: string
  }
}

export const updateReferenceDocument = WithSpan(
  'updateReferenceDocument',
  updateReferenceDocumentInternal,
)

async function updateReferenceDocumentInternal({
  authState,
  tenantId,
  projectId,
  referenceDocumentId,
  data,
}: UpdateReferenceDocumentParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.put(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${referenceDocumentId}`,
      data,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Update reference document error:', error)
    throw new Error('Failed to update reference document')
  }
}

interface SyncDocumentRagParams {
  authState: User
  tenantId: string
  projectId: string
  documentId: string
}

export const syncDocumentRag = WithSpan(
  'syncDocumentRag',
  syncDocumentRagInternal,
)

async function syncDocumentRagInternal({
  authState,
  tenantId,
  projectId,
  documentId,
}: SyncDocumentRagParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${documentId}/rag-sync`,
      {},
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Sync document RAG error:', error)
    throw new Error('Failed to sync document to RAG')
  }
}

interface CreateNewSOPParams {
  authState: User
  tenantId: string
  title: string
  projectId?: string | null
  referenceDocumentIds: string[]
  tag?: string | null
  category?: string | null
  processingResolution?: 'SD' | 'HD' | 'FHD' | 'Original'
  folder?: string
}

export const createNewSOP = WithSpan('createNewSOP', createNewSOPInternal)

async function createNewSOPInternal({
  authState,
  tenantId,
  title,
  projectId,
  referenceDocumentIds,
  tag,
  category,
  processingResolution,
  folder,
}: CreateNewSOPParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops`,
      {
        title,
        documentIds: referenceDocumentIds,
        folder,
        metadata: {
          type: tag,
          category,
          processingResolution,
        },
      },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Create SOP error:', error)
    throw new Error('Failed to create new SOP')
  }
}

interface UpdateSOPParams {
  authState: User
  tenantId: string
  projectId: string
  sopId: string
  data: {
    title?: string
    documentIds?: string[]
    metadata?: Record<string, unknown>
    folder?: string
  }
}

export const updateSOP = WithSpan('updateSOP', updateSOPInternal)

async function updateSOPInternal({
  authState,
  tenantId,
  projectId,
  sopId,
  data,
}: UpdateSOPParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.put(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${sopId}`,
      data,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Update SOP error:', error)
    throw new Error('Failed to update SOP')
  }
}

interface DeleteSOPParams {
  authState: User
  tenantId: string
  projectId: string
  sopId: string
}

export const deleteSOP = WithSpan('deleteSOP', deleteSOPInternal)

async function deleteSOPInternal({
  authState,
  tenantId,
  projectId,
  sopId,
}: DeleteSOPParams) {
  const token = await authState.getIdToken()
  try {
    const response = await client.delete(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${sopId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
    )
    return response.data
  } catch (error) {
    console.error('Delete SOP error:', error)
    throw new Error('Failed to delete SOP')
  }
}

interface GenerateSignedUrlParams {
  authState: User
  tenantId: string
  projectId: string
  filename: string
  contentType: string
  referenceDocumentId: string
  /** When true, the API records the GCS object path when the client finishes the PUT (avoids waiting on Pub/Sub). */
  setGcsUri?: boolean
}

export const generateSignedUploadUrl = WithSpan(
  'generateSignedUploadUrl',
  generateSignedUploadUrlInternal,
)

async function generateSignedUploadUrlInternal({
  authState,
  tenantId,
  projectId,
  filename,
  contentType,
  referenceDocumentId,
  setGcsUri,
}: GenerateSignedUrlParams) {
  const token = await authState.getIdToken()
  try {
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${referenceDocumentId}/upload-url`,
      { filename, contentType },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        withCredentials: true,
        ...(setGcsUri === true ? { params: { setGcsUri: true } } : {}),
      },
    )
    return response.data
  } catch (error) {
    console.error('Generate signed URL error:', error)
    throw new Error('Failed to generate signed URL')
  }
}

interface ProcessSOPParams {
  authState: User
  tenantId: string
  projectId: string
  sopId: string
  prompt?: string
  force?: boolean
  regenerateTitle?: boolean
  waitComplete?: boolean
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const processSOP = WithSpan('processSOP', processSOPInternal)

async function processSOPInternal({
  authState,
  tenantId,
  projectId,
  sopId,
  prompt,
  force = false,
  regenerateTitle = false,
  waitComplete = true,
}: ProcessSOPParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${sopId}/process`,
      { prompt, force, regenerateTitle },
      { headers, withCredentials: true },
    )
    return
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 502) {
        if (!waitComplete) {
          return
        }
        // This is a hard limitation of firebase rewrites(proxy)
        // See: https://firebase.google.com/docs/hosting/functions#direct-requests-to-function
        // process may take over 60 seconds to complete
        // so we need to poll the SOP until it is complete
        while (true) {
          const headers = await getMutationAuthHeaders(authState)
          const response = await client.get(
            `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${sopId}`,
            { headers, withCredentials: true },
          )

          if (response.data.status === 'PROCESSING') {
            await delay(10 * 1000)
          } else if (response.data.status === 'COMPLETED') {
            return
          } else {
            throw new Error('Failed to process SOP')
          }
        }
      }
    }
    console.error('Process SOP error:', error)
    throw new Error('Failed to process SOP')
  }
}

interface SaveDocumentParams {
  authState: User
  document: DocumentData
  tenantId: string
}

export const saveDocument = WithSpan('saveDocument', saveDocumentInternal)

async function saveDocumentInternal({
  authState,
  document,
  tenantId,
}: SaveDocumentParams) {
  try {
    const { title, metadata, content } = document
    const headers = await getMutationAuthHeaders(authState)
    const documentRefsIds =
      document.content?.steps?.map((step) => step.source) ?? []
    const response = await client.put(
      `${BASE_URL}/api/org/${tenantId}/projects/${document.projectId}/sops/${document.id}`,
      {
        title,
        documentIds: documentRefsIds,
        metadata,
        content,
      },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Document save error:', error)
    throw new Error('Failed to save document')
  }
}

interface PublishDocumentParams {
  authState: User
  tenantId: string
  projectId: string
  documentId: string
  isOrgAdmin: boolean
  note?: string
}

export const publishDocument = WithSpan(
  'publishDocument',
  publishDocumentInternal,
)

async function publishDocumentInternal({
  authState,
  tenantId,
  projectId,
  documentId,
  isOrgAdmin,
  note,
}: PublishDocumentParams) {
  if (!isOrgAdmin) {
    throw new Error('You are not authorized to publish documents')
  }
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${documentId}/publish`,
      { note },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Publish document error:', error)
    throw new Error('Failed to publish document')
  }
}

interface RollbackSOPParams {
  authState: User
  tenantId: string
  projectId: string
  sopId: string
  targetVersion: number
  note?: string
}

export const rollbackSOP = WithSpan('rollbackSOP', rollbackSOPInternal)

async function rollbackSOPInternal({
  authState,
  tenantId,
  projectId,
  sopId,
  targetVersion,
  note,
}: RollbackSOPParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${sopId}/rollback`,
      { targetVersion, note },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Rollback SOP error:', error)
    throw new Error('Failed to rollback SOP')
  }
}

interface DeleteSOPDraftParams {
  authState: User
  tenantId: string
  projectId: string
  documentId: string
}
export const deleteSOPDraft = WithSpan('deleteSOPDraft', deleteSOPDraftInternal)

async function deleteSOPDraftInternal({
  authState,
  tenantId,
  projectId,
  documentId,
}: DeleteSOPDraftParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.delete(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${documentId}/draft`,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Delete SOP draft error:', error)
    throw new Error('Failed to delete SOP draft')
  }
}

interface UploadFileToSignedUrlParams {
  signedUrl: string
  file: File
  onProgress?: (progress: number) => void
  onComplete?: () => void
  onError?: (error: Error, progress: number) => void
  abortController?: AbortController
}

export const uploadFileToSignedUrl = WithSpan(
  'uploadFileToSignedUrl',
  uploadFileToSignedUrlInternal,
)

async function uploadFileToSignedUrlInternal({
  signedUrl,
  file,
  onProgress,
  onComplete,
  onError,
  abortController,
}: UploadFileToSignedUrlParams) {
  let signal: AbortSignal | undefined
  if (abortController) {
    signal = abortController.signal
  }

  let internalUploadProgress = 0 // used to track the progress of the upload interally

  try {
    await client.put(signedUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percentComplete =
            (progressEvent.loaded / progressEvent.total) * 100
          onProgress(percentComplete)
          internalUploadProgress = percentComplete
        }
      },
      signal,
    })
    onComplete?.()
  } catch (error) {
    console.error('File upload error:', error)
    onError?.(error as Error, internalUploadProgress)
    throw new Error('Failed to upload file')
  }
}

interface CreateProjectParams {
  authState: User
  tenantId: string
  projectName: string
  description?: string
  metadata?: Record<string, unknown>
}

export const createProject = WithSpan('createProject', createProjectInternal)

async function createProjectInternal({
  authState,
  tenantId,
  projectName,
  description,
  metadata,
}: CreateProjectParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects`,
      { name: projectName, description, metadata },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Project creation error:', error)
    throw new Error('Failed to create project')
  }
}

interface RenameProjectParams {
  authState: User
  tenantId: string
  projectId: string
  newName?: string
  newDescription?: string
  newMetadata?: Record<string, unknown>
}

export const renameProject = WithSpan('renameProject', renameProjectInternal)

async function renameProjectInternal({
  authState,
  tenantId,
  projectId,
  newName,
  newDescription,
  newMetadata,
}: RenameProjectParams) {
  try {
    if (!newName && !newDescription && !newMetadata) {
      throw new Error('No changes to project')
    }
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.put(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}`,
      { name: newName, description: newDescription, metadata: newMetadata },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Project rename error:', error)
    throw new Error('Failed to workspace project')
  }
}

interface DeleteProjectParams {
  authState: User
  tenantId: string
  projectId: string
}

export const deleteProject = WithSpan('deleteProject', deleteProjectInternal)

async function deleteProjectInternal({
  authState,
  tenantId,
  projectId,
}: DeleteProjectParams) {
  try {
    const token = await authState.getIdToken()
    const response = await client.delete(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
    )
    return response.data
  } catch (error) {
    console.error('Project deletion error:', error)
    throw new Error('Failed to delete project')
  }
}

interface UpdateUserRoleParams {
  authState: User
  tenantId: string
  userId: string
  role: 'VIEWER' | 'EDITOR' | 'ADMIN'
}

export const updateUserRole = WithSpan('updateUserRole', updateUserRoleInternal)

async function updateUserRoleInternal({
  authState,
  tenantId,
  userId,
  role,
}: UpdateUserRoleParams) {
  try {
    const headers = await getPutAuthHeaders(authState)

    const userInfo = await getUserById({
      token: await authState.getIdToken(),
      tenantId,
      userId,
    })
    if (!userInfo) {
      throw new Error('User not found')
    }

    const url = `${BASE_URL}/api/org/${tenantId}/permissions/admin`
    const response = await client.post(
      url,
      {
        userId,
        resourcePath: `/org/${tenantId}`,
        role: role.toLowerCase(),
      },
      { headers, withCredentials: true },
    )
    return {
      response: response.data,
    }
  } catch (error) {
    console.error('Update user role error:', error)
    throw new Error('Failed to update user role')
  }
}

interface RevokeOrgRoleParams {
  authState: User
  tenantId: string
  userId: string
}

export const revokeOrgRole = WithSpan('revokeOrgRole', revokeOrgRoleInternal)

async function revokeOrgRoleInternal({
  authState,
  tenantId,
  userId,
}: RevokeOrgRoleParams) {
  try {
    const headers = await getPutAuthHeaders(authState)
    const headersWithContentType = {
      ...headers,
      'Content-Type': 'application/json',
    }

    const response = await client.delete(
      `${BASE_URL}/api/org/${tenantId}/permissions/admin/${userId}`,
      {
        data: { resourcePath: `/org/${tenantId}` },
        headers: headersWithContentType,
        withCredentials: true,
      },
    )
    return response.data
  } catch (error) {
    console.error('Revoke org role error:', error)
    throw new Error('Failed to revoke org role')
  }
}

interface UpdateUserWorkspaceAccessParams {
  authState: User
  tenantId: string
  userId: string
  projectRoles: Record<string, string>
  allProjectIds: string[]
}

export const updateUserWorkspaceAccess = WithSpan(
  'updateUserWorkspaceAccess',
  updateUserWorkspaceAccessInternal,
)

async function updateUserWorkspaceAccessInternal({
  authState,
  tenantId,
  userId,
  projectRoles,
  allProjectIds,
}: UpdateUserWorkspaceAccessParams) {
  try {
    const headers = await getPutAuthHeaders(authState)
    const headersWithContentType = {
      ...headers,
      'Content-Type': 'application/json',
    }

    const url = `${BASE_URL}/api/org/${tenantId}/permissions/admin`

    const deletePermission = async (resourcePath: string) => {
      return client.delete(
        `${BASE_URL}/api/org/${tenantId}/permissions/admin/${userId}`,
        {
          data: { resourcePath },
          headers: headersWithContentType,
          withCredentials: true,
        },
      )
    }

    const postPermissionUpdate = async (resourcePath: string, role: string) => {
      return client.post(
        url,
        { userId, resourcePath, role },
        { headers: headersWithContentType, withCredentials: true },
      )
    }

    // Update individual project permissions sequentially to avoid race conditions
    for (const projectId of allProjectIds) {
      const role = projectRoles[projectId]
      if (role) {
        await postPermissionUpdate(
          `/org/${tenantId}/projects/${projectId}`,
          role.toLowerCase(),
        )
      } else {
        await deletePermission(`/org/${tenantId}/projects/${projectId}`)
      }
    }

    return { success: true }
  } catch (error) {
    console.error('Update user workspace access error:', error)
    throw new Error('Failed to update user workspace access')
  }
}

// ── Project Member Mutations (project-scoped endpoints) ──────────────

export type ProjectMemberDiff = {
  added: { userId: string; role: string }[]
  updated: { userId: string; role: string }[]
  removed: { userId: string }[]
}

interface AddProjectMemberParams {
  authState: User
  tenantId: string
  projectId: string
  userId: string
  role: string
}

export const addProjectMember = WithSpan(
  'addProjectMember',
  addProjectMemberInternal,
)

async function addProjectMemberInternal({
  authState,
  tenantId,
  projectId,
  userId,
  role,
}: AddProjectMemberParams) {
  const headers = await getMutationAuthHeaders(authState)
  return client.post(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/members`,
    { userId, role: role.toLowerCase() },
    { headers, withCredentials: true },
  )
}

interface UpdateProjectMemberRoleParams {
  authState: User
  tenantId: string
  projectId: string
  userId: string
  role: string
}

export const updateProjectMemberRole = WithSpan(
  'updateProjectMemberRole',
  updateProjectMemberRoleInternal,
)

async function updateProjectMemberRoleInternal({
  authState,
  tenantId,
  projectId,
  userId,
  role,
}: UpdateProjectMemberRoleParams) {
  const headers = await getMutationAuthHeaders(authState)
  return client.put(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/members/${userId}`,
    { role: role.toLowerCase() },
    { headers, withCredentials: true },
  )
}

interface RemoveProjectMemberParams {
  authState: User
  tenantId: string
  projectId: string
  userId: string
}

export const removeProjectMember = WithSpan(
  'removeProjectMember',
  removeProjectMemberInternal,
)

async function removeProjectMemberInternal({
  authState,
  tenantId,
  projectId,
  userId,
}: RemoveProjectMemberParams) {
  const headers = await getMutationAuthHeaders(authState)
  return client.delete(
    `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/members/${userId}`,
    { headers, withCredentials: true },
  )
}

interface BatchUpdateProjectMembersParams {
  authState: User
  tenantId: string
  projectId: string
  diff: ProjectMemberDiff
}

export const batchUpdateProjectMembers = WithSpan(
  'batchUpdateProjectMembers',
  batchUpdateProjectMembersInternal,
)

async function batchUpdateProjectMembersInternal({
  authState,
  tenantId,
  projectId,
  diff,
}: BatchUpdateProjectMembersParams) {
  const errors: { userId: string; action: string; error: unknown }[] = []

  for (const { userId, role } of diff.added) {
    try {
      await addProjectMemberInternal({
        authState,
        tenantId,
        projectId,
        userId,
        role,
      })
    } catch (error) {
      errors.push({ userId, action: 'add', error })
    }
  }

  for (const { userId, role } of diff.updated) {
    try {
      await updateProjectMemberRoleInternal({
        authState,
        tenantId,
        projectId,
        userId,
        role,
      })
    } catch (error) {
      errors.push({ userId, action: 'update', error })
    }
  }

  for (const { userId } of diff.removed) {
    try {
      await removeProjectMemberInternal({
        authState,
        tenantId,
        projectId,
        userId,
      })
    } catch (error) {
      errors.push({ userId, action: 'remove', error })
    }
  }

  if (errors.length > 0) {
    const total = diff.added.length + diff.updated.length + diff.removed.length
    console.error('Batch update project members errors:', errors)
    throw new Error(`Failed to update ${errors.length} of ${total} member(s).`)
  }

  return { success: true }
}

export const batchUpdateProjectMembersAsOrgAdmin = WithSpan(
  'batchUpdateProjectMembersAsOrgAdmin',
  batchUpdateProjectMembersAsOrgAdminInternal,
)

async function batchUpdateProjectMembersAsOrgAdminInternal({
  authState,
  tenantId,
  projectId,
  diff,
}: BatchUpdateProjectMembersParams) {
  const headers = await getMutationAuthHeaders(authState)
  const url = `${BASE_URL}/api/org/${tenantId}/permissions/admin`
  const resourcePath = `/org/${tenantId}/projects/${projectId}`
  const errors: { userId: string; action: string; error: unknown }[] = []

  for (const { userId, role } of diff.added) {
    try {
      await client.post(
        url,
        { userId, resourcePath, role: role.toLowerCase() },
        { headers, withCredentials: true },
      )
    } catch (error) {
      errors.push({ userId, action: 'add', error })
    }
  }

  for (const { userId, role } of diff.updated) {
    try {
      await client.post(
        url,
        { userId, resourcePath, role: role.toLowerCase() },
        { headers, withCredentials: true },
      )
    } catch (error) {
      errors.push({ userId, action: 'update', error })
    }
  }

  for (const { userId } of diff.removed) {
    try {
      await client.delete(`${url}/${userId}`, {
        data: { resourcePath },
        headers,
        withCredentials: true,
      })
    } catch (error) {
      errors.push({ userId, action: 'remove', error })
    }
  }

  if (errors.length > 0) {
    const total = diff.added.length + diff.updated.length + diff.removed.length
    console.error('Batch update project members (org admin) errors:', errors)
    throw new Error(`Failed to update ${errors.length} of ${total} member(s).`)
  }

  return { success: true }
}

interface CreateUserParams {
  authState: User
  tenantId: string
  displayName: string | undefined
  email: string
  password: string
}

export const createUser = WithSpan('createUser', createUserInternal)

async function createUserInternal({
  authState,
  tenantId,
  displayName,
  email,
  password,
}: CreateUserParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)

    // Generate a unique UID
    const uid = crypto.randomUUID()

    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/users/create`,
      { uid, email, password, tenant: tenantId, displayName, role: 'VIEWER' },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Create user error:', error)
    throw new Error('Failed to create user')
  }
}

interface InviteUserParams {
  authState: User
  tenantId: string
  email: string
  displayName: string | undefined
  ssoOnly?: boolean
}

export const inviteUser = WithSpan('inviteUser', inviteUserInternal)

async function inviteUserInternal({
  authState,
  tenantId,
  email,
  displayName,
  ssoOnly,
}: InviteUserParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)

    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/users/invite`,
      { email, displayName, tenant: tenantId, ssoOnly },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Invite user error:', error)
    throw new Error('Failed to invite user')
  }
}

interface UpdateUserMeProfileParams {
  authState: User
  tenantId: string
  data: {
    displayName?: string
    phoneNumber?: string
    photoURL?: string
  }
}

export const updateUserMeProfile = WithSpan(
  'updateUserMeProfile',
  updateUserMeProfileInternal,
)

async function updateUserMeProfileInternal({
  authState,
  tenantId,
  data,
}: UpdateUserMeProfileParams) {
  try {
    const headers = await getPutAuthHeaders(authState)
    const response = await client.put(
      `${BASE_URL}/api/org/${tenantId}/users/me/profile`,
      data,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Update user me profile error:', error)
    throw new Error('Failed to update user me profile')
  }
}

interface DeleteUserParams {
  authState: User
  tenantId: string
  userId: string
  isOrgAdmin: boolean
}

export const deleteUser = WithSpan('deleteUser', deleteUserInternal)

async function deleteUserInternal({
  authState,
  tenantId,
  userId,
  isOrgAdmin,
}: DeleteUserParams) {
  if (!isOrgAdmin) {
    throw new Error('You are not authorized to delete users')
  }
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.delete(
      `${BASE_URL}/api/org/${tenantId}/users/${userId}`,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Delete user error:', error)
    throw new Error('Failed to delete user')
  }
}

interface BookmarkResourceParams {
  authState: User
  tenantId: string
  resourceType: ResourceType
  resourceId: string
}

export const bookmarkResource = WithSpan(
  'bookmarkResource',
  bookmarkResourceInternal,
)

async function bookmarkResourceInternal({
  authState,
  tenantId,
  resourceType,
  resourceId,
}: BookmarkResourceParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/bookmarks`,
      {
        resourceType,
        resourceId,
      },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Bookmark resource error:', error)
    throw new Error('Failed to bookmark resource')
  }
}

interface DeleteBookmarkResourceParams {
  authState: User
  tenantId: string
  resourceType: ResourceType
  resourceId: string
}

export const deleteBookmarkResource = WithSpan(
  'deleteBookmarkResource',
  deleteBookmarkResourceInternal,
)

async function deleteBookmarkResourceInternal({
  authState,
  tenantId,
  resourceType,
  resourceId,
}: DeleteBookmarkResourceParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.delete(
      `${BASE_URL}/api/org/${tenantId}/bookmarks/${resourceType}/${resourceId}`,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Delete bookmark resource error:', error)
    throw new Error('Failed to delete bookmark resource')
  }
}

interface UpdateOrgSettingsParams<S> {
  authState: User
  tenantId: string
  name: Name<S>
  data: Data<S>
}

export const updateOrgSettings = WithSpan(
  'updateOrgSettings',
  updateOrgSettingsInternal,
)

async function updateOrgSettingsInternal<S>({
  authState,
  tenantId,
  name,
  data,
}: UpdateOrgSettingsParams<S>) {
  try {
    const headers = await getPutAuthHeaders(authState)
    const response = await client.put(
      `${BASE_URL}/api/org/${tenantId}/settings/${name}`,
      data,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Update org settings error:', error)
    throw new Error('Failed to update org settings')
  }
}

interface SyncSOPRagParams {
  authState: User
  tenantId: string
  projectId: string
  sopId: string
}

export const syncSOPRag = WithSpan('syncSOPRag', syncSOPRagInternal)

async function syncSOPRagInternal({
  authState,
  tenantId,
  projectId,
  sopId,
}: SyncSOPRagParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${sopId}/rag-sync`,
      {},
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Sync SOP RAG error:', error)
    throw new Error('Failed to sync SOP to RAG')
  }
}

interface UnsyncSOPRagParams {
  authState: User
  tenantId: string
  projectId: string
  sopId: string
}

export const unsyncSOPRag = WithSpan('unsyncSOPRag', unsyncSOPRagInternal)

async function unsyncSOPRagInternal({
  authState,
  tenantId,
  projectId,
  sopId,
}: UnsyncSOPRagParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.delete(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${sopId}/rag-sync`,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Unsync SOP RAG error:', error)
    throw new Error('Failed to unsync SOP from RAG')
  }
}

interface MoveSOPParams {
  authState: User
  tenantId: string
  projectId: string
  sopId: string
  targetProjectId: string
}

export const moveSOP = WithSpan('moveSOP', moveSOPInternal)

async function moveSOPInternal({
  authState,
  tenantId,
  projectId,
  sopId,
  targetProjectId,
}: MoveSOPParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/sops/${sopId}/move`,
      { targetProjectId },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Move SOP error:', error)
    throw new Error('Failed to move document to workspace')
  }
}

interface MoveReferenceDocumentParams {
  authState: User
  tenantId: string
  projectId: string
  referenceDocumentId: string
  targetProjectId: string
}

export const moveReferenceDocument = WithSpan(
  'moveReferenceDocument',
  moveReferenceDocumentInternal,
)

async function moveReferenceDocumentInternal({
  authState,
  tenantId,
  projectId,
  referenceDocumentId,
  targetProjectId,
}: MoveReferenceDocumentParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/documents/${referenceDocumentId}/move`,
      { targetProjectId },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Move reference document error:', error)
    throw new Error('Failed to move document to workspace')
  }
}

export type MoveWorkspaceDestination = {
  targetProjectId: string
  targetFolder?: string
}

type WorkspaceMoveHandlers = {
  moveBetweenProjects: (
    sourceProjectId: string,
    targetProjectId: string,
  ) => Promise<void>
  setFolder: (projectId: string, folder: string) => Promise<void>
}

/** Orchestrates workspace/folder moves without coupling SOP and reference-document APIs. */
async function executeWorkspaceMove(
  sourceProjectId: string,
  destination: MoveWorkspaceDestination,
  handlers: WorkspaceMoveHandlers,
): Promise<void> {
  const targetFolder = destination.targetFolder ?? 'root'
  const { targetProjectId } = destination

  if (targetProjectId === sourceProjectId) {
    await handlers.setFolder(sourceProjectId, targetFolder)
    return
  }

  await handlers.moveBetweenProjects(sourceProjectId, targetProjectId)

  try {
    await handlers.setFolder(targetProjectId, targetFolder)
  } catch (folderError) {
    console.error('Set folder after move error:', folderError)
    try {
      await handlers.moveBetweenProjects(targetProjectId, sourceProjectId)
    } catch (rollbackError) {
      console.error('Move rollback after folder failure:', rollbackError)
      throw new Error(
        'The document was moved but could not be placed in the target folder, and automatic rollback failed. Refresh the page and try again.',
      )
    }
    throw new Error(
      'Could not place the document in the target folder. It was moved back to the original workspace.',
    )
  }
}

interface MoveSOPToWorkspaceParams {
  authState: User
  tenantId: string
  sourceProjectId: string
  sopId: string
  destination: MoveWorkspaceDestination
}

export const moveSOPToWorkspace = WithSpan(
  'moveSOPToWorkspace',
  moveSOPToWorkspaceInternal,
)

async function moveSOPToWorkspaceInternal({
  authState,
  tenantId,
  sourceProjectId,
  sopId,
  destination,
}: MoveSOPToWorkspaceParams): Promise<void> {
  await executeWorkspaceMove(sourceProjectId, destination, {
    moveBetweenProjects: (fromProjectId, toProjectId) =>
      moveSOPInternal({
        authState,
        tenantId,
        projectId: fromProjectId,
        sopId,
        targetProjectId: toProjectId,
      }),
    setFolder: (projectId, folder) =>
      updateSOPInternal({
        authState,
        tenantId,
        projectId,
        sopId,
        data: { folder },
      }),
  })
}

interface MoveReferenceDocumentToWorkspaceParams {
  authState: User
  tenantId: string
  sourceProjectId: string
  referenceDocumentId: string
  destination: MoveWorkspaceDestination
}

export const moveReferenceDocumentToWorkspace = WithSpan(
  'moveReferenceDocumentToWorkspace',
  moveReferenceDocumentToWorkspaceInternal,
)

async function moveReferenceDocumentToWorkspaceInternal({
  authState,
  tenantId,
  sourceProjectId,
  referenceDocumentId,
  destination,
}: MoveReferenceDocumentToWorkspaceParams): Promise<void> {
  await executeWorkspaceMove(sourceProjectId, destination, {
    moveBetweenProjects: (fromProjectId, toProjectId) =>
      moveReferenceDocumentInternal({
        authState,
        tenantId,
        projectId: fromProjectId,
        referenceDocumentId,
        targetProjectId: toProjectId,
      }),
    setFolder: (projectId, folder) =>
      updateReferenceDocumentInternal({
        authState,
        tenantId,
        projectId,
        referenceDocumentId,
        data: { folder },
      }),
  })
}

export type CustomExportFormat = 'xlsx' | 'docx'
export type ExportMode = 'published' | 'draft'

const CUSTOM_EXPORT_ACCEPT: Record<CustomExportFormat, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
}

interface ExportSOPCustomizedParams {
  authState: User
  tenantId: string
  projectId: string
  sopId: string
  format: CustomExportFormat
  mode: ExportMode
}

export interface ExportSOPCustomizedResult {
  blob: Blob
  filename: string
}

export const exportSOPCustomized = WithSpan(
  'exportSOPCustomized',
  exportSOPCustomizedInternal,
)

async function exportSOPCustomizedInternal({
  authState,
  tenantId,
  projectId,
  sopId,
  format,
  mode,
}: ExportSOPCustomizedParams): Promise<ExportSOPCustomizedResult> {
  try {
    const token = await authState.getIdToken()
    const response = await client.post(
      `${BASE_URL}/customize`,
      {
        tenant_id: tenantId,
        project_id: projectId,
        sop_id: sopId,
        mode,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: CUSTOM_EXPORT_ACCEPT[format],
        },
        responseType: 'blob',
      },
    )

    const contentDisposition = response.headers['content-disposition'] || ''
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    )
    const filename = filenameMatch
      ? filenameMatch[1].replace(/['"]/g, '')
      : `export.${format}`

    return { blob: response.data, filename }
  } catch (error) {
    console.error(`Export SOP as ${format.toUpperCase()} error:`, error)
    throw new Error(`Failed to export SOP as ${format.toUpperCase()}`)
  }
}

interface CreateFolderParams {
  authState: User
  tenantId: string
  projectId: string
  name: string
}

export const createFolder = WithSpan('createFolder', createFolderInternal)

async function createFolderInternal({
  authState,
  tenantId,
  projectId,
  name,
}: CreateFolderParams): Promise<Folder> {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/folders`,
      { name },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Create folder error:', error)
    if (error instanceof AxiosError) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message
      throw new Error(message)
    }
    throw new Error('Failed to create folder')
  }
}

interface RenameFolderParams {
  authState: User
  tenantId: string
  projectId: string
  folderName: string
  newName: string
}

export const renameFolder = WithSpan('renameFolder', renameFolderInternal)

async function renameFolderInternal({
  authState,
  tenantId,
  projectId,
  folderName,
  newName,
}: RenameFolderParams): Promise<Folder> {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.put(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/folders/${folderName}`,
      { name: newName },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Rename folder error:', error)
    if (error instanceof AxiosError) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message
      throw new Error(message)
    }
    throw new Error('Failed to rename folder')
  }
}

interface DeleteFolderParams {
  authState: User
  tenantId: string
  projectId: string
  folderName: string
}

export const deleteFolder = WithSpan('deleteFolder', deleteFolderInternal)

async function deleteFolderInternal({
  authState,
  tenantId,
  projectId,
  folderName,
}: DeleteFolderParams) {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.delete(
      `${BASE_URL}/api/org/${tenantId}/projects/${projectId}/folders/${folderName}`,
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Delete folder error:', error)
    if (error instanceof AxiosError) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message
      throw new Error(message)
    }
    throw new Error('Failed to delete folder')
  }
}

interface CopyDocumentToTenantParams {
  authState: User
  tenantId: string
  sourceDocumentId: string
  destinationTenantId: string
  destinationProjectId: string
}

export const copyDocumentToTenant = WithSpan(
  'copyDocumentToTenant',
  copyDocumentToTenantInternal,
)

async function copyDocumentToTenantInternal({
  authState,
  tenantId,
  sourceDocumentId,
  destinationTenantId,
  destinationProjectId,
}: CopyDocumentToTenantParams): Promise<CopyDocumentResponse> {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/copy/document`,
      { sourceDocumentId, destinationTenantId, destinationProjectId },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Copy document to tenant error:', error)
    throw new Error('Failed to copy document to organization')
  }
}

interface CopySOPToTenantParams {
  authState: User
  tenantId: string
  sourceSOPId: string
  destinationTenantId: string
  destinationProjectId: string
  copyDocuments: boolean
}

export const copySOPToTenant = WithSpan(
  'copySOPToTenant',
  copySOPToTenantInternal,
)

async function copySOPToTenantInternal({
  authState,
  tenantId,
  sourceSOPId,
  destinationTenantId,
  destinationProjectId,
  copyDocuments,
}: CopySOPToTenantParams): Promise<DocumentData> {
  try {
    const headers = await getMutationAuthHeaders(authState)
    const response = await client.post(
      `${BASE_URL}/api/org/${tenantId}/copy/sop`,
      { sourceSOPId, destinationTenantId, destinationProjectId, copyDocuments },
      { headers, withCredentials: true },
    )
    return response.data
  } catch (error) {
    console.error('Copy SOP to tenant error:', error)
    throw new Error('Failed to copy SOP to organization')
  }
}
